// Live progress strip rendered above the composer. Reads
// `controller.currentPlan` + `controller.currentStep` — both maintained by
// CopilotController as it parses each turn's assistant_text (see
// planParser.js). Hidden when no parsable plan is present.
//
// Three render modes:
//   - hidden: no parsable plan → returns null.
//   - expanded: full step list with done / active / pending icons.
//   - collapsed: single-line summary. Clicking re-expands.
//
// State machine:
//   - New plan body arrives (currentPlan changes) → expand.
//   - Step reaches N/N (parsed `current === total`) → auto-collapse to the
//     "done" summary so the strip gets out of the way once the task is over.
//   - User clicks the bar to toggle either direction at any time.
//
// Layout: lives inside `.copilot-composer-layer` above the Composer so the
// strip sits next to the input rather than at the top of the thread. A
// ResizeObserver publishes the strip's height into the `--copilot-plan-h`
// CSS var; the thread viewport's padding-bottom + the feather gradient both
// consume it so the last assistant bubble never hides behind the strip.

import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { Check, ChevronDown, ChevronUp, Circle, Loader2, X } from "lucide-react";
import { useCopilotController } from "../CopilotRuntimeProvider";
import { parsePlanSteps, parseCurrentStep } from "../planParser";

function StepIcon({ state }) {
  if (state === "done") {
    return <Check className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-emerald-500" aria-hidden="true" />;
  }
  if (state === "active") {
    return <Loader2 className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-animate-spin tw-text-accent-foreground" aria-hidden="true" />;
  }
  return <Circle className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-muted-foreground" aria-hidden="true" />;
}

export const PlanProgress = observer(function PlanProgress() {
  const controller = useCopilotController();
  const rootRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const steps = parsePlanSteps(controller.currentPlan);
  const stepInfo = parseCurrentStep(controller.currentStep);
  const done = stepInfo != null && stepInfo.current === stepInfo.total;

  // Reset to expanded + un-dismissed whenever a new plan body arrives. The
  // controller wipes currentPlan to "" on every new user message, so the
  // next non-empty plan body unambiguously means "new task started".
  useEffect(() => {
    setCollapsed(false);
    setDismissed(false);
  }, [controller.currentPlan]);

  // Auto-collapse when the task reaches its final step.
  useEffect(() => {
    if (done) setCollapsed(true);
  }, [done]);

  // Publish the strip's height to `.copilot-root` as --copilot-plan-h so
  // the thread viewport reserves space above the composer for it. Cleanup
  // (component unmount when no parsable plan) resets it to 0px so the
  // viewport reclaims that padding.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const panel = el.closest(".copilot-root");
    if (!panel) return undefined;
    let lastH = -1;
    let rafId = 0;
    const update = () => {
      const h = el.offsetHeight;
      if (h === lastH) return;
      lastH = h;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        panel.style.setProperty("--copilot-plan-h", `${h}px`);
        rafId = 0;
      });
    };
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => {
      ro.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      panel.style.setProperty("--copilot-plan-h", "0px");
    };
  }, [collapsed, steps.length]);

  if (steps.length === 0 || dismissed) return null;

  const currentIndex = stepInfo?.current ?? null;

  // Dismiss button — shared between collapsed and expanded modes. Hides the
  // strip until the next <plan> body arrives. The handler stops propagation
  // so the X never doubles as "toggle collapse" when nested in the bar.
  const dismissButton = (
    <button
      type="button"
      className="copilot-plan-progress__dismiss"
      onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
      aria-label="Dismiss plan progress"
      title="Dismiss"
    >
      <X className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
    </button>
  );

  if (collapsed) {
    const label = done
      ? `${stepInfo.total}/${stepInfo.total} done${stepInfo.label ? ` — ${stepInfo.label}` : ""}`
      : (stepInfo
          ? `Step ${stepInfo.current} of ${stepInfo.total}${stepInfo.label ? ` — ${stepInfo.label}` : ""}`
          : "Plan");
    // Outer is a div (not a button) so the X dismiss can sit alongside the
    // expand-on-click area without nesting buttons (invalid HTML).
    return (
      <div
        ref={rootRef}
        className={
          "copilot-plan-progress copilot-plan-progress--collapsed" +
          (done ? " copilot-plan-progress--done" : "")
        }
      >
        <button
          type="button"
          className="copilot-plan-progress__expand"
          onClick={() => setCollapsed(false)}
          aria-expanded="false"
          aria-label="Show plan progress"
        >
          {done ? (
            <Check className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-emerald-500" aria-hidden="true" />
          ) : (
            <Loader2 className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-animate-spin tw-text-accent-foreground" aria-hidden="true" />
          )}
          <span className="copilot-plan-progress__summary">{label}</span>
          <ChevronUp className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-opacity-70" aria-hidden="true" />
        </button>
        {dismissButton}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="copilot-plan-progress"
      role="region"
      aria-label="Plan progress"
    >
      <div className="copilot-plan-progress__header">
        <span className="copilot-plan-progress__header-text">
          {stepInfo
            ? `Step ${stepInfo.current} of ${stepInfo.total}${stepInfo.label ? ` — ${stepInfo.label}` : ""}`
            : "Plan"}
        </span>
        <div className="copilot-plan-progress__header-actions">
          <button
            type="button"
            className="copilot-plan-progress__toggle"
            onClick={() => setCollapsed(true)}
            aria-expanded="true"
            aria-label="Collapse plan progress"
          >
            <ChevronDown className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
          </button>
          {dismissButton}
        </div>
      </div>
      <ol className="copilot-plan-progress__list">
        {steps.map((step) => {
          let state = "pending";
          if (currentIndex != null) {
            if (step.index < currentIndex) state = "done";
            else if (step.index === currentIndex) state = "active";
          }
          // When the task itself is done, the final step gets a checkmark
          // too — the model emits "Current step: N/N" then exits without
          // emitting "N+1/N", so the active-on-current rule above leaves
          // the final step as "active" forever otherwise.
          if (done && step.index === stepInfo.total) state = "done";
          return (
            <li
              key={step.index}
              className={`copilot-plan-progress__item copilot-plan-progress__item--${state}`}
              aria-current={state === "active" ? "step" : undefined}
            >
              <StepIcon state={state} />
              <span className="copilot-plan-progress__text">{step.text}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
});
